import { IAttachableTextInputPropsWithBuilder, AttachableTextInputPropsWithBuilder } from "./attachableProps/textInput";
import { IAttachableCheckboxPropsWithBuilder, AttachableCheckboxPropsWithBuilder } from "./attachableProps/checkbox";
import {
	ComboBoxIntermediateState,
	TabProps,
	ValidationErrorProps,
	RichTextEditorIntermediateStateHandlers,
	MentionInputIntermediateStateHandlers
} from "client/Components";
import { IValueReducer, IObjectFormReducer, ICollectionFormReducer } from "./reducer";
import {
	IValueValidation,
	InputValidationResult,
	InputValidationResultKind,
	IObjectValidation,
	ICollectionValidation,
	InvalidInputValidationResult,
	IRootValidation
} from "client/Validation/input";
import { IFormHandler } from ".";
import { never } from "client_legacy/ndaw/errors";
import { isNil, isNotNil } from "client/Misc/utils";
import { CombinedInputValidationResult, InputValidationResultErrorSource } from "client/Validation/state";
import { AttachableFileSelectorPropsWithBuilder, IAttachableFileSelectorPropsWithBuilder } from "./attachableProps/fileSelector";
import {
	AttachableRichTextEditorPropsWithBuilder,
	IResolveAttachableRichTextEditorPropsWithBuilder
} from "./attachableProps/richTextEditor";
import { IValidationStep } from "client/Validation/steps";
import { AttachableTagSelectProps, AttachTagSelectOptions, IAttachableTagSelectProps } from "./attachableProps/tagSelect";
import { TagSaveModel } from "client/Api/Tags/tags";
import { AttachableColorPickerPropsWithBuilder, IAttachableColorPickerPropsWithBuilder } from "./attachableProps/colorPicker";
import { AttachableFontStylePickerPropsWithBuilder, IAttachableFontStylePickerPropsWithBuilder } from "./attachableProps/FontStylePicker";
import {
	AttachableMentionInputPropsWithBuilder,
	IResolveAttachableMentionInputPropsWithBuilder
} from "./attachableProps/mentionInput";
import {
	isTextWithMentions,
	TextWithMentions,
	MentionData
} from "client/Components/MentionInput/converters";
import {
	AttachableComboBoxPropsWithBuilder,
	IAttachableComboBoxPropsWithBuilder
} from "./attachableProps/comboBox";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";
import {
	AttachableDatePickerPropsWithBuilder,
	IAttachableDatePickerPropsWithBuilder
} from "./attachableProps/datePicker";

type IAttachableReorderableListPanelItemProps = {
	validationMessage?: string;
	trackingId?: string;
};

export interface IInputAttachable<TValue> {
	attachToTextInput(): IAttachableTextInputPropsWithBuilder<TValue>;
	attachToTextArea(): IAttachableTextInputPropsWithBuilder<TValue>;
	attachToCheckbox(): IAttachableCheckboxPropsWithBuilder<TValue>;
	attachToRadioButtonGroup(): IAttachableCheckboxPropsWithBuilder<TValue>;
	attachToSecretValueInput(): IAttachableTextInputPropsWithBuilder<TValue>;
	attachToDatePicker(): IAttachableDatePickerPropsWithBuilder<TValue>;
	attachToSelect(): IAttachableCheckboxPropsWithBuilder<TValue>;
	attachToValidationError(
		ignoreChildErrors?: boolean
	): ValidationErrorProps;
	attachToReorderableListPanelItem(
		ignoreChildErrors?: boolean
	): IAttachableReorderableListPanelItemProps;
	attachToFileSelector(): IResolvedAttachableFileSelectorPropsWithBuilder<TValue>;
	attachToRichTextEditor(
		intermediateStateHandlers: RichTextEditorIntermediateStateHandlers
	): IResolveAttachableRichTextEditorPropsWithBuilder<TValue>;
	attachToRichTextEditorToolbar(
		intermediateStateHandlers: RichTextEditorIntermediateStateHandlers
	): IResolveAttachableRichTextEditorPropsWithBuilder<TValue>;
	attachToRichTextEditorContent(
		intermediateStateHandlers: RichTextEditorIntermediateStateHandlers
	): IResolveAttachableRichTextEditorPropsWithBuilder<TValue>;
	attachToMentionInput(
		intermediateStateHandlers: MentionInputIntermediateStateHandlers,
		mentionData: readonly MentionData[]
	): IResolveAttachableMentionInputPropsWithBuilder<TValue>;
	attachToColorPicker(): IAttachableColorPickerPropsWithBuilder<TValue>;
	attachToFontStylePicker(): IAttachableFontStylePickerPropsWithBuilder<TValue>;
	attachToComboBox(
		attachableHandler: ComboBoxIntermediateState<TValue>,
		defaultValue: TValue
	): IAttachableComboBoxPropsWithBuilder<TValue>;

	readonly reducer: IValueReducer<TValue>;
	readonly validation: IValueValidation<TValue>;
	readonly value: TValue;
}

export interface IAggregateAttachable {
	readonly validationState: InputValidationResult;
	attachToValidationError(): Exclude<ValidationErrorProps, "trackingId">;
	attachToTab(): Pick<TabProps, "hasValidationError">;
}

interface IObjectInputAttachable<TValue> extends IInputAttachable<TValue> {
	for<TProperty extends StringKeyOf<TValue>>(propertyName: TProperty): IValueInputAttachable<TValue[TProperty]>;
	when<TTrueResult, TFalseResult = false>(
		predicate: (value: TValue) => boolean,
		trueMapper: (conditionalHandler: IValueInputAttachable<TValue>) => TTrueResult,
		falseMapper?: (conditionalHandler: IValueInputAttachable<TValue>) => TFalseResult
	): TTrueResult | TFalseResult | false;
	whenSet<TTrueResult, TFalseResult = false>(
		trueMapper: (ensuredHandler: IValueInputAttachable<Exclude<TValue, nil>>) => TTrueResult,
		falseMapper?: (ensuredHandler: IValueInputAttachable<TValue & nil>) => TFalseResult,
	): TTrueResult | TFalseResult | false;
	ensureIsSet<TTrueResult>(
		mapper: (ensuredHandler: IValueInputAttachable<Exclude<TValue, nil>>) => TTrueResult
	): TTrueResult | false;
	whenType<TConditionalValue extends TValue, TTrueResult, TFalseResult = false>(
		typeGuardPredicate: (value: TValue) => value is TConditionalValue,
		trueMapper: (conditionalHandler: IValueInputAttachable<TConditionalValue>) => TTrueResult,
		falseMapper?: (conditionalHandler: IValueInputAttachable<Exclude<TValue, TConditionalValue>>) => TFalseResult
	): TTrueResult | TFalseResult;
	under<TProperty extends StringKeyOf<TValue>>(...propertyNames: TProperty[]): IAggregateAttachable;
	assertType<TConditionalValue extends TValue>(
		typeGuardPredicate: (value: TValue) => value is TConditionalValue
	): IValueInputAttachable<TConditionalValue>;
}

type IResolvedAttachableTagSelectProps<TCollectionItem, TTagValue extends number> = TCollectionItem extends TagSaveModel
	? IAttachableTagSelectProps<TTagValue>
	: never;

export interface ICollectionInputAttachable<TCollectionItem, TCollection> extends IInputAttachable<TCollection> {
	forIndex(index: number): IValueInputAttachable<TCollectionItem>;
	map<TResult>(mapper: (itemHandler: IValueInputAttachable<TCollectionItem>, index: number) => TResult): ReadonlyArray<TResult>;
	forEach(action: (itemHandler: IValueInputAttachable<TCollectionItem>, index: number) => void): void;
	attachToCheckboxGroup(): IAttachableCheckboxPropsWithBuilder<TCollection>;
	attachToMultiComboBox(): IAttachableCheckboxPropsWithBuilder<TCollection>;
	attachToMultiSelect(): IAttachableCheckboxPropsWithBuilder<TCollection>;
	attachToTagSelect<TTagValue extends number>(options: AttachTagSelectOptions): IResolvedAttachableTagSelectProps<TCollectionItem, TTagValue>;
	when<TTrueResult, TFalseResult = false>(
		predicate: (value: TCollection) => boolean,
		trueMapper: (conditionalHandler: IValueInputAttachable<TCollection>) => TTrueResult,
		falseMapper?: (conditionalHandler: IValueInputAttachable<TCollection>) => TFalseResult
	): TTrueResult | TFalseResult | false;
}

type IValueInputAttachable<TValue> =
	[TValue] extends [PrimitiveType] ? IInputAttachable<TValue> :
	[TValue] extends [ReadonlyArray<infer R>] ? ICollectionInputAttachable<R, TValue> :
	IObjectInputAttachable<TValue>;

class InputAttachable<TCollectionItem, TValue extends ReadonlyArray<TCollectionItem>> implements
	IObjectInputAttachable<TValue>,
	ICollectionInputAttachable<TCollectionItem, TValue>,
	IFormHandler<TValue> {

	public readonly reducer: IValueReducer<TValue>;

	public get value(): TValue {
		return this.reducer.value as TValue;
	}
	public readonly validation: IRootValidation<TValue> & (IValueValidation<TValue> | undefined);

	constructor(reducer: IValueReducer<TValue>, validation?: IValueValidation<TValue>) {
		this.reducer = reducer;
		this.validation = validation as IRootValidation<TValue> & IValueValidation<TValue>;
	}

	public assertType<TConditionalValue extends TValue>(
		typeGuardPredicate: (value: TValue) => value is TConditionalValue
	): IValueInputAttachable<TConditionalValue> {
		if (!typeGuardPredicate(this.value)) {
			throw new Error("Type does not match.");
		}

		return this as unknown as IValueInputAttachable<TConditionalValue>;
	}

	public attachToTextInput(): IAttachableTextInputPropsWithBuilder<TValue> {
		return new AttachableTextInputPropsWithBuilder(this.reducer, this.validation as IValueValidation<TValue>);
	}

	public attachToTextArea(): IAttachableTextInputPropsWithBuilder<TValue> {
		return new AttachableTextInputPropsWithBuilder(this.reducer, this.validation as IValueValidation<TValue>);
	}

	public attachToCheckbox(): IAttachableCheckboxPropsWithBuilder<TValue> {
		return new AttachableCheckboxPropsWithBuilder<TValue>(this.reducer, this.validation);
	}

	public attachToRadioButtonGroup(): IAttachableCheckboxPropsWithBuilder<TValue> {
		return new AttachableCheckboxPropsWithBuilder<TValue>(this.reducer, this.validation);
	}

	public attachToSecretValueInput(): IAttachableTextInputPropsWithBuilder<TValue> {
		return new AttachableTextInputPropsWithBuilder(this.reducer, this.validation as IValueValidation<TValue>);
	}

	public attachToDatePicker(): IAttachableDatePickerPropsWithBuilder<TValue> {
		return new AttachableDatePickerPropsWithBuilder(this.reducer, this.validation as IValueValidation<TValue>);
	}

	public attachToSelect(): IAttachableCheckboxPropsWithBuilder<TValue> {
		return new AttachableCheckboxPropsWithBuilder(this.reducer, this.validation as IValueValidation<TValue>);
	}

	public attachToCheckboxGroup(): IAttachableCheckboxPropsWithBuilder<TValue> {
		return new AttachableCheckboxPropsWithBuilder(this.reducer, this.validation as IValueValidation<TValue>);
	}

	public attachToMultiComboBox(): IAttachableCheckboxPropsWithBuilder<TValue> {
		return new AttachableCheckboxPropsWithBuilder(this.reducer, this.validation as IValueValidation<TValue>);
	}

	public attachToMultiSelect(): IAttachableCheckboxPropsWithBuilder<TValue> {
		return new AttachableCheckboxPropsWithBuilder(this.reducer, this.validation as IValueValidation<TValue>);
	}

	public attachToValidationError(
		ignoreChildErrors?: boolean
	): ValidationErrorProps {
		const validationState: CombinedInputValidationResult = this.validation.getValidationState();
		const trackingId: string = `${this.reducer.path}__validation`;

		if (validationState.kind === InputValidationResultKind.Invalid) {
			const isChildError: boolean = validationState.errorSource === InputValidationResultErrorSource.ChildError;

			const shouldIgnoreError: boolean = ignoreChildErrors === true && isChildError;

			if (!shouldIgnoreError) {
				return {
					message: validationState.errorMessage,
					trackingId
				};
			}
		}

		return {
			message: undefined,
			trackingId
		};
	}

	public attachToReorderableListPanelItem(
		ignoreChildErrors?: boolean
	): IAttachableReorderableListPanelItemProps {
		const validationState: CombinedInputValidationResult = this.validation.getValidationState();
		const trackingId: string = `${this.reducer.path}__validation`;

		if (validationState.kind === InputValidationResultKind.Invalid) {
			const isChildError: boolean = validationState.errorSource === InputValidationResultErrorSource.ChildError;

			const shouldIgnoreError: boolean = ignoreChildErrors === true && isChildError;

			if (!shouldIgnoreError) {
				return {
					validationMessage: validationState.errorMessage,
					trackingId
				};
			}
		}

		return {
			validationMessage: undefined,
			trackingId
		};
	}

	public attachToFileSelector(): IResolvedAttachableFileSelectorPropsWithBuilder<TValue> {
		const {
			value
		} = this.reducer;

		if (!isFileValue(value)) {
			throw new Error("Expected file value at this point");
		}

		return new AttachableFileSelectorPropsWithBuilder(
			this.reducer as unknown as IValueReducer<OptionalModel<Base64FileUriUploadWithMetadataModel | null>>,
			this.validation as unknown as IValueValidation<OptionalModel<Base64FileUriUploadWithMetadataModel | null>>
		) as unknown as IResolvedAttachableFileSelectorPropsWithBuilder<TValue>;
	}

	public attachToRichTextEditor(
		intermediateStateHandlers: RichTextEditorIntermediateStateHandlers
	): IResolveAttachableRichTextEditorPropsWithBuilder<TValue> {
		const {
			value
		} = this.reducer;

		if (typeof value !== "string" && !isNil(value)) {
			throw new Error("Expected string value at this point");
		}

		return new AttachableRichTextEditorPropsWithBuilder(
			this.reducer as unknown as IValueReducer<string | nil>,
			this.validation as unknown as IValueValidation<string | nil>,
			intermediateStateHandlers,
			""
		) as unknown as IResolveAttachableRichTextEditorPropsWithBuilder<TValue>;
	}

	public attachToRichTextEditorToolbar(
		intermediateStateHandlers: RichTextEditorIntermediateStateHandlers
	): IResolveAttachableRichTextEditorPropsWithBuilder<TValue> {
		const {
			value
		} = this.reducer;

		if (typeof value !== "string" && !isNil(value)) {
			throw new Error("Expected string value at this point");
		}

		return new AttachableRichTextEditorPropsWithBuilder(
			this.reducer as unknown as IValueReducer<string | nil>,
			this.validation as unknown as IValueValidation<string | nil>,
			intermediateStateHandlers,
			"__toolbar"
		) as unknown as IResolveAttachableRichTextEditorPropsWithBuilder<TValue>;
	}

	public attachToRichTextEditorContent(
		intermediateStateHandlers: RichTextEditorIntermediateStateHandlers
	): IResolveAttachableRichTextEditorPropsWithBuilder<TValue> {
		const {
			value
		} = this.reducer;

		if (typeof value !== "string" && !isNil(value)) {
			throw new Error("Expected string value at this point");
		}

		return new AttachableRichTextEditorPropsWithBuilder(
			this.reducer as unknown as IValueReducer<string | nil>,
			this.validation as unknown as IValueValidation<string | nil>,
			intermediateStateHandlers,
			"__content"
		) as unknown as IResolveAttachableRichTextEditorPropsWithBuilder<TValue>;
	}

	public attachToMentionInput(
		intermediateStateHandlers: MentionInputIntermediateStateHandlers,
		mentionData: readonly MentionData[]
	): IResolveAttachableMentionInputPropsWithBuilder<TValue> {
		const {
			value
		} = this.reducer;

		if (!isTextWithMentions(value)) {
			throw new Error("Expected value of type TextWithUserMentions at this point");
		}

		return new AttachableMentionInputPropsWithBuilder(
			this.reducer as unknown as IValueReducer<TextWithMentions>,
			this.validation as unknown as IValueValidation<TextWithMentions>,
			intermediateStateHandlers,
			mentionData
		) as unknown as IResolveAttachableMentionInputPropsWithBuilder<TValue>;
	}

	public attachToTagSelect<TTagValue extends number>(options: AttachTagSelectOptions)
		: IResolvedAttachableTagSelectProps<
			TCollectionItem,
			TTagValue
		> {
		const {
			value
		} = this.reducer;

		const {
			maxTagCount,
			maxTagLength,
			doesSupportDelete = true,
			color,
			onError
		} = options;

		if (!Array.isArray(value)) {
			throw new Error("Expected an array at this point");
		}

		if (value.length > 0) {
			const firstItem: unknown = value[0];
			if (typeof firstItem !== "object" || firstItem === null) {
				throw new Error("Expected an object at this point");
			}

			const maybeTagSaveModel: TagSaveModel = firstItem as TagSaveModel;
			if (
				typeof maybeTagSaveModel.name !== "string"
					|| (
						typeof maybeTagSaveModel.id !== "number"
							&& maybeTagSaveModel.id !== null
					)
			) {
				throw new Error("Expected a TagSaveModel instance at this point");
			}
		}

		return new AttachableTagSelectProps(
			this.reducer as unknown as IValueReducer<readonly TagSaveModel[]>,
			this.validation as unknown as IValueValidation<readonly TagSaveModel[]>,
			maxTagCount,
			maxTagLength,
			doesSupportDelete,
			color,
			onError
		) as unknown as IResolvedAttachableTagSelectProps<TCollectionItem, TTagValue>;
	}

	public attachToColorPicker(): IAttachableColorPickerPropsWithBuilder<TValue> {
		return new AttachableColorPickerPropsWithBuilder(this.reducer, this.validation as IValueValidation<TValue>);
	}

	public attachToFontStylePicker(): IAttachableFontStylePickerPropsWithBuilder<TValue> {
		return new AttachableFontStylePickerPropsWithBuilder(this.reducer, this.validation as IValueValidation<TValue>);
	}

	public attachToComboBox(
		attachableHandler: ComboBoxIntermediateState<TValue>,
		defaultValue: TValue,
		areEqual?: (value1: TValue, value2: TValue) => boolean
	): IAttachableComboBoxPropsWithBuilder<TValue> {
		return new AttachableComboBoxPropsWithBuilder(
			this.reducer,
			this.validation as IValueValidation<TValue>,
			attachableHandler,
			defaultValue,
			areEqual
		);
	}

	public when<
		TTrueResult,
		TFalseResult = false
	>(
		predicate: (value: TValue) => boolean,
		trueMapper: (conditionalHandler: IValueInputAttachable<TValue>) => TTrueResult,
		falseMapper?: (conditionalHandler: IValueInputAttachable<TValue>) => TFalseResult
	): TTrueResult | TFalseResult | false {
		if (predicate(this.value)) {
			return trueMapper(this as unknown as IValueInputAttachable<TValue>);
		}

		if (falseMapper) {
			return falseMapper(this as unknown as IValueInputAttachable<TValue>);
		}

		return false;
	}

	public whenSet<
		TTrueResult,
		TFalseResult = false
	>(
		trueMapper: (ensuredHandler: IValueInputAttachable<Exclude<TValue, nil>>) => TTrueResult,
		falseMapper?: (ensuredHandler: IValueInputAttachable<TValue & nil>) => TFalseResult
	): TTrueResult | TFalseResult | false {
		if (isNotNil(this.value)) {
			return trueMapper(this as unknown as IValueInputAttachable<Exclude<TValue, nil>>);
		}

		if (falseMapper) {
			return falseMapper(this as unknown as IValueInputAttachable<TValue & nil>);
		}

		return false;
	}

	public ensureIsSet<
		TTrueResult
	>(
		trueMapper: (ensuredHandler: IValueInputAttachable<Exclude<TValue, nil>>) => TTrueResult
	): TTrueResult | false {
		if (isNotNil(this.value)) {
			return trueMapper(this as unknown as IValueInputAttachable<Exclude<TValue, nil>>);
		}

		throw new Error("Value cannot be nil.");
	}

	public whenType<
		TConditionalValue extends TValue,
		TTrueResult,
		TFalseResult = false
	>(
		typeGuardPredicate: (value: TValue) => value is TConditionalValue,
		trueMapper: (conditionalHandler: IValueInputAttachable<TConditionalValue>) => TTrueResult,
		falseMapper?: (conditionalHandler: IValueInputAttachable<Exclude<TValue, TConditionalValue>>) => TFalseResult
	): TTrueResult | TFalseResult {
		if (typeGuardPredicate(this.value)) {
			return trueMapper(this as unknown as IValueInputAttachable<TConditionalValue>);
		}

		if (falseMapper) {
			return falseMapper(this as unknown as IValueInputAttachable<Exclude<TValue, TConditionalValue>>);
		}

		return false as unknown as TFalseResult;
	}


	public for<TProperty extends StringKeyOf<TValue>>(
		propertyName: TProperty
	): IValueInputAttachable<TValue[TProperty]> {
		const reducer = this.reducer as IObjectFormReducer<TValue>;
		const validation = this.validation as IObjectValidation<TValue>;
		const childAttachable = new InputAttachable<unknown, any>(reducer.for(propertyName), validation.for(propertyName));

		return childAttachable as unknown as IValueInputAttachable<TValue[TProperty]>;
	}

	public forIndex(index: number): IValueInputAttachable<TCollectionItem> {
		const reducer = this.reducer as ICollectionFormReducer<TCollectionItem, TValue>;
		const validation = this.validation as unknown as ICollectionValidation<TCollectionItem>;
		const childAttachable = new InputAttachable<unknown, any>(reducer.forIndex(index), validation.forIndex(index));

		return childAttachable as unknown as IValueInputAttachable<TCollectionItem>;
	}

	public map<TResult>(mapper: (itemHandler: IValueInputAttachable<TCollectionItem>, index: number) => TResult): ReadonlyArray<TResult> {
		const reducer = this.reducer as ICollectionFormReducer<TCollectionItem, TValue>;
		const validation = this.validation as unknown as ICollectionValidation<TCollectionItem>;

		return reducer.value.map((_, index) => {
			const childAttachable = new InputAttachable<unknown, any>(reducer.forIndex(index), validation.forIndex(index));
			return mapper(childAttachable as unknown as IValueInputAttachable<TCollectionItem>, index);
		});
	}

	public forEach(action: (itemHandler: IValueInputAttachable<TCollectionItem>, index: number) => void): void {
		const reducer = this.reducer as ICollectionFormReducer<TCollectionItem, TValue>;
		const validation = this.validation as unknown as ICollectionValidation<TCollectionItem>;

		return reducer.value.forEach((_, index) => {
			const childAttachable = new InputAttachable<unknown, any>(reducer.forIndex(index), validation.forIndex(index));
			action(childAttachable as unknown as IValueInputAttachable<TCollectionItem>, index);
		});
	}

	public under<TProperty extends StringKeyOf<TValue>>(...propertyNames: TProperty[]): IAggregateAttachable {
		const states: InputValidationResult[] = propertyNames.length === 0
			? [this.validation.getValidationState()]
			: propertyNames.map(x => this.validation.for(x).getValidationState());

		return new AggregateAttachable(states);
	}

	public forStep(step: IValidationStep): IAggregateAttachable {
		const states: readonly CombinedInputValidationResult[] = this.validation.getValidationStepStates(step);
		return new AggregateAttachable(states);
	}
}

class AggregateAttachable implements IAggregateAttachable {
	public readonly validationState: InputValidationResult;

	constructor(states: readonly InputValidationResult[]) {
		const invalidStates: InvalidInputValidationResult[] = states
			.filter(x => x.kind === InputValidationResultKind.Invalid) as unknown as InvalidInputValidationResult[];

		this.validationState = invalidStates.length > 0
			? invalidStates[0]
			: { kind: InputValidationResultKind.Valid };
	}

	public attachToValidationError(): Exclude<ValidationErrorProps, "trackingId"> {
		const validationState: InputValidationResult = this.validationState;
		switch (validationState.kind)
		{
			case InputValidationResultKind.Invalid:
				return {
					message: validationState.errorMessage
				};
			case InputValidationResultKind.Valid: {
				return {
					message: undefined
				};
			}
			default:
				never(validationState);
		}
	}

	public attachToTab(): Pick<TabProps, "hasValidationError"> {
		const validationState: InputValidationResult = this.validationState;
		switch (validationState.kind)
		{
			case InputValidationResultKind.Invalid:
				return {
					hasValidationError: true
				};
			case InputValidationResultKind.Valid: {
				return {
					hasValidationError: false
				};
			}
			default:
				never(validationState);
		}
	}
}

type IResolvedAttachableFileSelectorPropsWithBuilder<TFileValue> = TFileValue extends OptionalModel<Base64FileUriUploadWithMetadataModel>
	? IAttachableFileSelectorPropsWithBuilder
	: never;

function isFileValue(value: unknown): value is OptionalModel<Base64FileUriUploadWithMetadataModel> {
	if (isNil(value)) {
		return false;
	}

	const maybeTypedValue = value as Partial<OptionalModel<Base64FileUriUploadWithMetadataModel>>;

	if (maybeTypedValue.isSet === false) {
		return true;
	}

	if (maybeTypedValue.isSet !== true) {
		return false;
	}

	if (maybeTypedValue.value === null) {
		return true;
	}

	if (maybeTypedValue.value === undefined) {
		return false;
	}

	const {
		filename,
		uri
	} = maybeTypedValue.value;

	return typeof filename === "string" && typeof uri === "string";
}

export {
	InputAttachable,
	type IObjectInputAttachable,
	type IValueInputAttachable
};